import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { FundsApiServiceSupabase } from '../api/FundsApiSupabase';
import { useUserAccounts } from './UserAccountsContext';
import { useAccountData } from './AccountDataContext';
import { useAuth } from './AuthenticationContext';
const FundsContext = createContext(undefined);
export const FundsProvider = ({ children, storage }) => {
    const fundsApi = useMemo(() => new FundsApiServiceSupabase(storage), [storage]);
    const [loaded, setLoaded] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [fundsStats, setFundsStats] = useState({});
    const { selectedAccountId } = useUserAccounts();
    const { loaded: accountDataLoaded, accountData } = useAccountData();
    const { authenticated } = useAuth();
    const calculateFundsStats = useCallback((transactions) => {
        let totalDeposits = 0;
        let totalWithdrawals = 0;
        const currentBalance = (accountData && accountData.balance) || 0;
        let allTimeBalance = currentBalance;
        let allTimeProfit = currentBalance;
        transactions.forEach(transaction => {
            // Handle both old (type) and new (transaction_type) field names
            const transactionType = transaction.transaction_type || transaction.type;
            if (transactionType === 'deposit') {
                const depositAmount = Math.abs(transaction.amount);
                totalDeposits += depositAmount;
                allTimeProfit -= depositAmount;
            }
            else if (transactionType === 'withdrawal') {
                const withdrawalAmount = Math.abs(transaction.amount);
                totalWithdrawals += withdrawalAmount;
                allTimeBalance += withdrawalAmount;
                allTimeProfit += withdrawalAmount;
            }
        });
        const equity = totalDeposits - totalWithdrawals;
        const profit = currentBalance - equity;
        const unrealizedProfit = currentBalance - (totalDeposits - totalWithdrawals);
        return {
            totalDeposits,
            totalWithdrawals,
            currentBalance,
            profit,
            unrealizedProfit,
            allTimeBalance,
            allTimeProfit,
        };
    }, [accountData]);
    const load = useCallback(async (accountId, ignoreCache) => {
        console.log("Loading funds data from Supabase for account", accountId);
        try {
            setLoaded(false);
            if (!accountDataLoaded)
                return;
            const response = await fundsApi.getFundsTransactions(accountId, ignoreCache);
            if (!response) {
                console.log("No funds transactions");
                setTransactions([]);
                setFundsStats({});
                setLoaded(true);
                return;
            }
            let transactions = response.transactions;
            setTransactions(transactions);
            setFundsStats(calculateFundsStats(transactions));
            setLoaded(true);
        }
        catch (error) {
            console.error('Error loading funds data:', error);
            setLoaded(true);
        }
    }, [accountDataLoaded, calculateFundsStats, fundsApi]);
    useEffect(() => {
        if (!authenticated)
            return;
        if (!selectedAccountId)
            return;
        load(selectedAccountId, false);
    }, [authenticated, selectedAccountId, load]);
    const add = useCallback(async (accountId, type, amount, date) => {
        try {
            await fundsApi.addTransaction(accountId, type, amount, date);
            await load(accountId, true);
        }
        catch (error) {
            console.error('Error adding transaction:', error);
            throw error;
        }
    }, [load, fundsApi]);
    const update = useCallback(async (accountId, id, type, amount, date) => {
        try {
            await fundsApi.updateTransaction(accountId, id, type, amount, date);
            await load(accountId, true);
        }
        catch (error) {
            console.error('Error updating transaction:', error);
            throw error;
        }
    }, [load, fundsApi]);
    const refresh = useCallback(async (accountId) => {
        await load(accountId, true);
    }, [load]);
    const contextValue = {
        loaded,
        transactions,
        fundsStats,
        add,
        update,
        refresh,
    };
    return _jsx(FundsContext.Provider, { value: contextValue, children: children });
};
export const useFunds = () => {
    const context = useContext(FundsContext);
    if (context === undefined) {
        throw new Error('useFunds must be used within a FundsProvider');
    }
    return context;
};
